/**
 * nudgeFeedback — close-the-loop for the RotationNudgePrompt.
 *
 * Data model (all localStorage, all `ghostcomm_` prefixed so the
 * panic button wipes them):
 *
 *   ghostcomm_nudge_pending
 *     JSON array of { peerId, peerUsername, sentAt, peerLatestAtSent }
 *     — one entry per nudge we've sent that hasn't yet been resolved.
 *
 *   ghostcomm_nudges_worked
 *     Numeric counter of nudges where the peer actually rotated
 *     within the 48-hour feedback window. Surfaced in Settings.
 *
 * Resolution rules inside `checkPendingNudges()`:
 *   - If the peer's latest-rotation timestamp is now strictly
 *     greater than `peerLatestAtSent` AND was laid down within 48h
 *     of `sentAt`, we credit the sender with a "worked" and show a
 *     one-time celebratory toast.
 *   - If >48h have elapsed since `sentAt` and no rotation happened,
 *     we silently drop the pending entry — no toast, no regret.
 *   - Entries for peers whose latest-rotation timestamp hasn't
 *     changed yet but are still inside the window stay pending for
 *     a later check.
 */
import { toast } from 'sonner';
import { fetchLatestRotationAt } from '../hooks/useRotationHealth';

const PENDING_KEY = 'ghostcomm_nudge_pending';
const COUNTER_KEY = 'ghostcomm_nudges_worked';
const SENT_COUNTER_KEY = 'ghostcomm_nudges_sent_total';
const FEEDBACK_WINDOW_MS = 48 * 60 * 60 * 1000;

const _readPending = () => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};

const _writePending = (arr) => {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(arr)); }
  catch { /* quota / private mode — silent */ }
};

/** Counter getter + setter used by the Settings UI. */
export const nudgesWorkedCount = () => {
  try {
    const n = Number(localStorage.getItem(COUNTER_KEY) || '0');
    return Number.isNaN(n) ? 0 : n;
  } catch { return 0; }
};

/**
 * Lifetime count of nudges sent from this device. Cumulative — not
 * reset when pending entries close out, so the SecurityHalo card
 * can show "Sent: N  Worked: M  Pending: K" with M + K ≤ N.
 */
export const nudgesSentTotal = () => {
  try {
    const n = Number(localStorage.getItem(SENT_COUNTER_KEY) || '0');
    return Number.isNaN(n) ? 0 : n;
  } catch { return 0; }
};

/**
 * Pending (in-window, unresolved) nudge count for the halo card's
 * live strip. Filters out entries that have aged past the 48h
 * feedback window even if the watcher hasn't swept them yet.
 */
export const pendingNudgeCount = () => {
  const list = _readPending();
  const now = Date.now();
  return list.filter((e) => now - (e.sentAt || 0) < FEEDBACK_WINDOW_MS).length;
};

const _incrementCounter = () => {
  try {
    const next = nudgesWorkedCount() + 1;
    localStorage.setItem(COUNTER_KEY, String(next));
    return next;
  } catch { return nudgesWorkedCount(); }
};

const _incrementSentCounter = () => {
  try {
    const next = nudgesSentTotal() + 1;
    localStorage.setItem(SENT_COUNTER_KEY, String(next));
    return next;
  } catch { return nudgesSentTotal(); }
};

/**
 * Record that we just nudged a peer. Called from
 * `RotationNudgePrompt.sendNudge` immediately after the message is
 * confirmed delivered.
 */
export const recordNudge = ({ peerId, peerUsername, peerLatestAtSent }) => {
  if (!peerId) return;
  const list = _readPending();
  // Collapse duplicates for the same peer — the latest send wins
  // so the feedback window extends from the most recent nudge.
  const filtered = list.filter((e) => e.peerId !== peerId);
  filtered.push({
    peerId,
    peerUsername: peerUsername || 'your contact',
    sentAt: Date.now(),
    peerLatestAtSent: peerLatestAtSent || 0,
  });
  _writePending(filtered);
  _incrementSentCounter();
};

/**
 * Iterate pending nudges, resolving each against the current
 * peer rotation timestamp. Called from the Dashboard mount effect
 * (once per session) so a loop of refresh → re-fire doesn't
 * spam /key-history in duress or healthy cases.
 *
 * Returns the number of nudges freshly credited so callers can
 * decide whether to re-render Settings.
 */
export const checkPendingNudges = async () => {
  // Defense-in-depth duress guard — even though fetchLatestRotationAt
  // is duress-aware, skipping the whole loop is cleaner.
  if (typeof window !== 'undefined' &&
      sessionStorage.getItem('gs_duress_mode') === '1') return 0;

  const list = _readPending();
  if (list.length === 0) return 0;

  const now = Date.now();
  const keep = [];
  let credited = 0;

  for (const entry of list) {
    const windowEnd = entry.sentAt + FEEDBACK_WINDOW_MS;
    if (now > windowEnd) continue; // drop silently — window closed

    const latest = await fetchLatestRotationAt(entry.peerId);
    if (latest && latest > (entry.peerLatestAtSent || 0)) {
      credited += 1;
      _incrementCounter();
      toast.success(
        `${entry.peerUsername} rotated after your nudge 👏`,
        { duration: 5000 }
      );
      continue; // resolved — don't keep it
    }
    keep.push(entry);
  }

  _writePending(keep);
  return credited;
};
